import React, { useMemo } from 'react';

import { Web } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button, SubHeader, Footer } from '../../components';
import useIsMobileScreen from '../../hooks/useCheckIsMobile';

import './styles.scoped.css';

const AdminPortal = () => {
  const menuData = useSelector(state => state.admin.menuData);
  const user = useSelector(state => state.admin.user);
  const navigate = useNavigate();
  const isMobile = useIsMobileScreen();

  const isGlobalAdmin = useMemo(() => {
    const appAccessItems = user?.appAccess?.items;
    if (!appAccessItems) return false;
    return appAccessItems.some(app => app?.appId?.id === '5' && app?.roleId?.id === '6');
  }, [user]);

  // CP-857: BI Admin (role 37) maintains the BI reference tables and nothing else. Global
  // Admin sees every section, so it covers this one too.
  const isBiAdmin = useMemo(() => {
    const appAccessItems = user?.appAccess?.items;
    if (!appAccessItems) return false;
    return appAccessItems.some(app => app?.appId?.id === '5' && app?.roleId?.id === '37');
  }, [user]);

  // The per-application admins (Driver Qualifications 11, Quote Engine 12, Dynamic Pricing
  // 13, Expense Entry 14) are the roles the Access screens are for. BI Admin is not one of
  // them, and verifyRoute denies it /client-users, so the section is hidden rather than
  // shown as a button that fails.
  const isAppAdmin = useMemo(() => {
    const appAccessItems = user?.appAccess?.items;
    if (!appAccessItems) return false;
    return appAccessItems.some(app => app?.appId?.id === '5' && ['11', '12', '13', '14'].includes(app?.roleId?.id));
  }, [user]);

  const filteredMenuData = useMemo(
    () =>
      menuData
        .filter(section => {
          if (section.header === 'Configurations' || section.header === 'Document Management') {
            return isGlobalAdmin;
          }
          if (section.header === 'BI Administration') {
            return isGlobalAdmin || isBiAdmin;
          }
          if (section.header === 'Access') {
            return isGlobalAdmin || isAppAdmin;
          }
          return true;
        })
        .map(section => {
          if (section.header === 'Access') {
            return {
              ...section,
              items: section.items.filter(item => {
                if (item.title === 'Corporate Access') {
                  return isGlobalAdmin;
                }
                return true;
              }),
            };
          }
          return section;
        }),
    [menuData, isGlobalAdmin, isBiAdmin, isAppAdmin],
  );

  return (
    <>
      <SubHeader
        text="Admin Portal"
        buttons={[
          {
            icon: <Web className="web-icon-color" sx={{ color: '#fff', fontSize: 30 }} />,
            onPress: () => navigate(-1),
          },
        ]}
      />

      <div className="wrap">
        {filteredMenuData.map(item => (
          <div className="section" key={`${item.header}-section`}>
            <h1 className="header" key={`${item.header}-header`}>
              {item.header}
            </h1>
            <span className="row" key={`${item.header}-span`}>
              {item.items.map(i => (
                <Button
                  key={i.title}
                  variant="admin"
                  style={{ minWidth: isMobile ? '320px' : '390px', marginRight: 40, marginTop: 12, marginBottom: 12 }}
                  onPress={() => navigate(i.to)}
                  text={i.title}
                />
              ))}
            </span>
          </div>
        ))}
      </div>
      <Footer />
    </>
  );
};

export default AdminPortal;
